import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, AlertTriangle } from "lucide-react";
import { payrollApi } from "../../services/payrollApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StoreSelect } from "../../components/StoreSelect";
import { KpiStrip } from "../../components/KpiStrip";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "../../lib/utils";
import { formatApiErrorDetail } from "../../lib/api";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { useLocalization } from "../../hooks/useLocalization";

const TABS = ["periods", "wages", "rules"];
const QUALIFYING_CATEGORIES = [
  "REGULAR_HOURS", "OVERTIME_HOURS", "LATE_NIGHT_HOURS", "STATUTORY_HOLIDAY_HOURS", "NON_STATUTORY_HOLIDAY_HOURS",
  "OPENING_SHIFT_HOURS", "CLOSING_SHIFT_HOURS", "FULL_SHIFT_COMPLETION_HOURS", "CROSS_MIDNIGHT_HOURS",
  "QUALIFYING_FULL_SHIFT_PREMIUM_HOURS",
];

/** Flags rules that overlap (same classification + store scope + both active) so a reviewer
 * can spot conflicting/duplicate premium rules without cross-referencing manually (audit item 12). */
function findOverlappingRuleIds(rules) {
  const active = rules.filter((r) => r.status === "APPROVED" && !r.effective_to);
  const conflicts = new Set();
  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i], b = active[j];
      if (a.applies_to_classification === b.applies_to_classification && (a.store_location || null) === (b.store_location || null) && (a.position || null) === (b.position || null)) {
        conflicts.add(a.id); conflicts.add(b.id);
      }
    }
  }
  return conflicts;
}

export default function PayrollDashboardPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canPreparePayroll, canReviewPayroll, canViewPayroll } = usePermissions();
  const [tab, setTab] = useState("periods");
  const [periods, setPeriods] = useState([]);
  const [wageProfiles, setWageProfiles] = useState([]);
  const [rules, setRules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newMonth, setNewMonth] = useState("");
  const [newPaymentDate, setNewPaymentDate] = useState("");
  const [wageForm, setWageForm] = useState({ employee_id: "", hourly_rate: "", transportation_allowance: "0", effective_from: "", notes: "" });
  const [ruleForm, setRuleForm] = useState({ rule_name: "", description: "", applies_to_classification: "LATE_NIGHT_HOURS", rate_percent: "", effective_from: "", source_document: "", store_location: "", position: "" });
  const [rejectDialog, setRejectDialog] = useState(null); // { kind: "wage"|"rule", id, context }

  const load = () => {
    setLoading(true);
    Promise.all([
      payrollApi.listPeriods().then((r) => setPeriods(r.data)).catch(() => {}),
      payrollApi.listWageProfiles().then((r) => setWageProfiles(r.data)).catch(() => {}),
      payrollApi.listRules().then((r) => setRules(r.data)).catch(() => {}),
      payrollApi.listEmployeesForPayroll().then((r) => setEmployees(r.data)).catch(() => {}),
      payrollApi.dashboardSummary().then((r) => setSummary(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { if (canViewPayroll) load(); }, [canViewPayroll]);
  useRouteRefresh(load);

  if (!canViewPayroll) return <p className="text-sm text-gray-500" data-testid="payroll-access-denied">{t("mgmtPayroll.accessDenied")}</p>;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const overlappingRuleIds = findOverlappingRuleIds(rules);

  const createPeriod = async () => {
    try {
      const { data } = await payrollApi.createPeriod({ payroll_month: newMonth, payment_date: newPaymentDate });
      toast.success(t("mgmtPayroll.periodCreated"));
      setNewMonth(""); setNewPaymentDate(""); load();
      navigate(`/management/payroll/periods/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createWageProfile = async () => {
    try {
      await payrollApi.createWageProfile(wageForm);
      toast.success(t("mgmtPayroll.wageProfileCreated"));
      setWageForm({ employee_id: "", hourly_rate: "", transportation_allowance: "0", effective_from: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const wageAction = async (id, action) => {
    try {
      if (action === "submit") await payrollApi.submitWageProfile(id);
      if (action === "approve") await payrollApi.approveWageProfile(id);
      toast.success(t("mgmtPayroll.wageProfileUpdated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openRejectWage = (w) => setRejectDialog({
    kind: "wage", id: w.id,
    title: t("mgmtPayroll.rejectWageTitle"),
    contextItems: [
      { label: t("mgmtPayroll.dialogEmployee"), value: employeeName(w.employee_id) },
      { label: t("mgmtPayroll.dialogCompType"), value: w.compensation_type === "FIXED_ASSIGNMENT" ? t("mgmtPayroll.fixedAssignmentPay") : t("mgmtPayroll.hourlyWage") },
      { label: t("mgmtPayroll.dialogAmountRate"), value: `\u00a5${w.hourly_rate}${w.compensation_type === "FIXED_ASSIGNMENT" ? ` ${t("mgmtPayroll.perApprovedAssignment")}` : t("mgmtPayroll.perHour")}` },
      { label: t("mgmtPayroll.dialogEffectiveDate"), value: w.effective_from },
      { label: t("mgmtPayroll.dialogSourceEvidence"), value: w.notes || "-" },
      { label: t("mgmtPayroll.dialogApprovalStatus"), value: t(`statuses.${w.status}`) },
    ],
  });

  const openRejectRule = (r) => setRejectDialog({
    kind: "rule", id: r.id,
    title: t("mgmtPayroll.rejectRuleTitle"),
    contextItems: [
      { label: t("mgmtPayroll.dialogRule"), value: r.rule_name },
      { label: t("mgmtPayroll.colClassification"), value: r.applies_to_classification.replaceAll("_", " ") },
      { label: t("mgmtPayroll.colRate"), value: `${r.rate_percent}%` },
      { label: t("mgmtPayroll.dialogScope"), value: `${r.store_location || t("mgmtPayroll.allStoresLabel")}${r.position ? ` \u00b7 ${r.position}` : ""}` },
      { label: t("mgmtPayroll.dialogSourceDoc"), value: r.source_document || "-" },
      { label: t("mgmtPayroll.dialogApprovalStatus"), value: t(`statuses.${r.status}`) },
    ],
  });

  const confirmReject = async (reason) => {
    if (rejectDialog.kind === "wage") await payrollApi.rejectWageProfile(rejectDialog.id, reason);
    if (rejectDialog.kind === "rule") await payrollApi.rejectRule(rejectDialog.id, reason);
    toast.success(t("mgmtPayroll.rejectedWithReason"));
    load();
  };

  const createRule = async () => {
    try {
      await payrollApi.createRule({ ...ruleForm, store_location: ruleForm.store_location || undefined, position: ruleForm.position || undefined });
      toast.success(t("mgmtPayroll.ruleCreated"));
      setRuleForm({ rule_name: "", description: "", applies_to_classification: "LATE_NIGHT_HOURS", rate_percent: "", effective_from: "", source_document: "", store_location: "", position: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const ruleAction = async (id, action) => {
    try {
      if (action === "approve") await payrollApi.approveRule(id);
      toast.success(t("mgmtPayroll.ruleUpdated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const kpiItems = summary ? [
    { label: t("mgmtPayroll.kpiCurrentPeriod"), value: summary.current_period?.payroll_month || "-", testId: "kpi-current-period", onClick: summary.current_period ? () => navigate(`/management/payroll/periods/${summary.current_period.period_id}`) : undefined },
    { label: t("mgmtPayroll.kpiPeriodStatus"), value: summary.current_period?.status ? t(`statuses.${summary.current_period.status}`) : "-", testId: "kpi-period-status", tone: summary.current_period?.status === "PUBLISHED" ? "success" : "default" },
    { label: t("mgmtPayroll.kpiTimecardsBlocked"), value: summary.current_period?.timecards_blocked ?? "-", testId: "kpi-timecards-blocked", tone: (summary.current_period?.timecards_blocked || 0) > 0 ? "danger" : "success" },
    { label: t("mgmtPayroll.kpiLinesReview"), value: summary.current_period?.lines_requiring_review ?? "-", testId: "kpi-lines-review", tone: (summary.current_period?.lines_requiring_review || 0) > 0 ? "warning" : "success", onClick: summary.current_period ? () => navigate(`/management/payroll/periods/${summary.current_period.period_id}/lines`) : undefined },
    { label: t("mgmtPayroll.kpiWagePending"), value: summary.wage_profiles_pending_approval, testId: "kpi-wage-pending", tone: summary.wage_profiles_pending_approval > 0 ? "warning" : "default", onClick: () => setTab("wages") },
    { label: t("mgmtPayroll.kpiRulesPending"), value: summary.rules_pending_approval, testId: "kpi-rules-pending", tone: summary.rules_pending_approval > 0 ? "warning" : "default", onClick: () => setTab("rules") },
    { label: t("mgmtPayroll.kpiPaymentsPending"), value: summary.current_period?.payment_confirmations_pending ?? "-", testId: "kpi-payments-pending", tone: (summary.current_period?.payment_confirmations_pending || 0) > 0 ? "warning" : "success", onClick: summary.current_period ? () => navigate(`/management/payroll/periods/${summary.current_period.period_id}/payments`) : undefined },
    { label: t("mgmtPayroll.kpiDisputesOpen"), value: summary.disputes_open, testId: "kpi-disputes-open", tone: summary.disputes_open > 0 ? "danger" : "success" },
  ] : [];

  return (
    <div className="space-y-5" data-testid="payroll-dashboard-page">
      <PageHelpBanner pageKey="payroll-dashboard" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtPayroll.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtPayroll.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtPayroll.subtitle")}</p>
      </div>

      {loading && !summary ? <Skeleton className="h-24 w-full rounded-xl" data-testid="payroll-kpi-skeleton" /> : summary && <KpiStrip items={kpiItems} testId="payroll-kpi-strip" />}

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((tid) => (
          <button key={tid} data-testid={`payroll-tab-${tid}`} onClick={() => setTab(tid)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === tid ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
            {tid === "periods" ? t("mgmtPayroll.tabPeriods") : tid === "wages" ? t("mgmtPayroll.tabWages") : t("mgmtPayroll.tabRules")}
          </button>
        ))}
      </div>

      {tab === "periods" && (
        <div className="space-y-4">
          {canPreparePayroll && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="create-period-form">
              <div className="space-y-1"><label className="text-xs text-gray-500">{t("mgmtPayroll.payrollMonthLabel")}</label><Input data-testid="period-month-input" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} placeholder="2026-06" className="w-32" /></div>
              <div className="space-y-1">
                <label className="text-xs text-gray-500 block">{t("mgmtPayroll.paymentDateLabel")}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="period-payment-date-input" className={cn("justify-between gap-2 min-w-[160px] font-normal", !newPaymentDate && "text-gray-500")}>
                      {newPaymentDate ? format(new Date(`${newPaymentDate}T00:00:00`), "PPP") : t("mgmtPayroll.selectDate")}
                      <CalendarIcon size={14} className="text-gray-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={newPaymentDate ? new Date(`${newPaymentDate}T00:00:00`) : undefined}
                      onSelect={(d) => setNewPaymentDate(d ? format(d, "yyyy-MM-dd") : "")}
                      data-testid="period-payment-date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Button data-testid="create-period-btn" onClick={createPeriod}>{t("mgmtPayroll.createPeriod")}</Button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colMonth")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colPaymentDate")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100" data-testid={`period-row-${p.id}`}>
                    <td className="px-4 py-2 font-semibold text-gray-800">{p.payroll_month}</td>
                    <td className="px-4 py-2 text-gray-600">{p.payment_date}</td>
                    <td className="px-4 py-2"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-2"><button data-testid={`open-period-btn-${p.id}`} onClick={() => navigate(`/management/payroll/periods/${p.id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtPayroll.open")}</button></td>
                  </tr>
                ))}
                {periods.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-periods-row">{t("mgmtPayroll.noPeriods")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "wages" && (
        <div className="space-y-4">
          {canPreparePayroll && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="create-wage-profile-form">
              <Select value={wageForm.employee_id} onValueChange={(v) => setWageForm({ ...wageForm, employee_id: v })}>
                <SelectTrigger data-testid="wage-employee-select"><SelectValue placeholder={t("mgmtPayroll.employeePlaceholder")} /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.legal_name} ({e.employee_id})</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="wage-hourly-rate-input" value={wageForm.hourly_rate} onChange={(e) => setWageForm({ ...wageForm, hourly_rate: e.target.value })} placeholder={t("mgmtPayroll.hourlyRatePlaceholder")} />
              <Input data-testid="wage-transportation-input" value={wageForm.transportation_allowance} onChange={(e) => setWageForm({ ...wageForm, transportation_allowance: e.target.value })} placeholder={t("mgmtPayroll.transportationPlaceholder")} />
              <Input data-testid="wage-effective-from-input" type="date" value={wageForm.effective_from} onChange={(e) => setWageForm({ ...wageForm, effective_from: e.target.value })} />
              <Button data-testid="create-wage-profile-btn" onClick={createWageProfile}>{t("mgmtPayroll.createDraft")}</Button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colCompensationType")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colAmount")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEffectiveFrom")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colActions")}</th></tr></thead>
              <tbody>
                {wageProfiles.map((w) => {
                  const isFixed = w.compensation_type === "FIXED_ASSIGNMENT";
                  return (
                  <tr key={w.id} className="border-t border-gray-100" data-testid={`wage-profile-row-${w.id}`}>
                    <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(w.employee_id)}</td>
                    <td className="px-4 py-2 text-gray-600">{isFixed ? t("mgmtPayroll.fixedAssignmentPay") : t("mgmtPayroll.hourlyWage")}</td>
                    <td className="px-4 py-2 text-gray-600">¥{w.hourly_rate}{isFixed ? ` ${t("mgmtPayroll.perApprovedAssignment")}` : t("mgmtPayroll.perHour")}</td>
                    <td className="px-4 py-2 text-gray-600">{w.effective_from}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={w.status} />
                      {w.compliance_status && w.compliance_status.includes("CONFIRMED") ? (
                        <p className="mt-1 text-[11px] font-semibold text-emerald-600" data-testid={`wage-compliance-flag-${w.id}`}>{t("mgmtPayroll.complianceConfirmed")}</p>
                      ) : w.compliance_status ? (
                        <div className="mt-1 text-[11px] font-bold text-red-600 flex items-center gap-1" data-testid={`wage-compliance-flag-${w.id}`}>
                          <AlertTriangle size={11} /> {w.compliance_status === "MINIMUM_WAGE_CONFLICT_REVIEW_REQUIRED" ? t("mgmtPayroll.minWageConflict") : w.compliance_status.replaceAll("_", " ")}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 space-x-1">
                      {w.status === "DRAFT" && canPreparePayroll && <Button size="sm" variant="outline" data-testid={`wage-submit-btn-${w.id}`} onClick={() => wageAction(w.id, "submit")}>{t("mgmtPayroll.submit")}</Button>}
                      {w.status === "PENDING_APPROVAL" && canReviewPayroll && (
                        <>
                          <Button size="sm" data-testid={`wage-approve-btn-${w.id}`} onClick={() => wageAction(w.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtPayroll.approve")}</Button>
                          <Button size="sm" variant="outline" data-testid={`wage-reject-btn-${w.id}`} onClick={() => openRejectWage(w)} className="text-red-600 border-red-200 hover:bg-red-50">{t("mgmtPayroll.reject")}</Button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {wageProfiles.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-wage-profiles-row">{t("mgmtPayroll.noWageProfiles")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "rules" && (
        <div className="space-y-4">
          {canPreparePayroll && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="create-rule-form">
              <Input data-testid="rule-name-input" value={ruleForm.rule_name} onChange={(e) => setRuleForm({ ...ruleForm, rule_name: e.target.value })} placeholder={t("mgmtPayroll.ruleNamePlaceholder")} />
              <Select value={ruleForm.applies_to_classification} onValueChange={(v) => setRuleForm({ ...ruleForm, applies_to_classification: v })}>
                <SelectTrigger data-testid="rule-classification-select"><SelectValue /></SelectTrigger>
                <SelectContent>{QUALIFYING_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="rule-rate-input" value={ruleForm.rate_percent} onChange={(e) => setRuleForm({ ...ruleForm, rate_percent: e.target.value })} placeholder={t("mgmtPayroll.ratePercentPlaceholder")} />
              <Input data-testid="rule-effective-from-input" type="date" value={ruleForm.effective_from} onChange={(e) => setRuleForm({ ...ruleForm, effective_from: e.target.value })} />
              <StoreSelect testId="rule-store-input" value={ruleForm.store_location} onChange={(v) => setRuleForm({ ...ruleForm, store_location: v })} allowEmpty emptyLabel={t("mgmtPayroll.allStoresLabel")} className="px-3 py-1.5" />
              <Input data-testid="rule-position-input" value={ruleForm.position} onChange={(e) => setRuleForm({ ...ruleForm, position: e.target.value })} placeholder={t("mgmtPayroll.positionPlaceholder")} />
              <Input data-testid="rule-source-input" value={ruleForm.source_document} onChange={(e) => setRuleForm({ ...ruleForm, source_document: e.target.value })} placeholder={t("mgmtPayroll.sourceDocPlaceholder")} className="col-span-2" />
              <Input data-testid="rule-description-input" value={ruleForm.description} onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })} placeholder={t("mgmtPayroll.descriptionPlaceholder")} className="col-span-2" />
              <Button data-testid="create-rule-btn" onClick={createRule}>{t("mgmtPayroll.createRule")}</Button>
              {(ruleForm.applies_to_classification === "STATUTORY_HOLIDAY_HOURS" || ruleForm.applies_to_classification === "NON_STATUTORY_HOLIDAY_HOURS") && (
                <p className="text-xs text-amber-700 col-span-full" data-testid="holiday-not-configured-notice">{t("mgmtPayroll.holidayNotConfigured")}</p>
              )}
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colRule")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colClassification")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colScope")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colRate")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colVersion")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colActions")}</th></tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className={cn("border-t border-gray-100", overlappingRuleIds.has(r.id) && "bg-amber-50/60")} data-testid={`rule-row-${r.id}`}>
                    <td className="px-4 py-2 font-semibold text-gray-800 flex items-center gap-1.5">
                      {r.rule_name}
                      {overlappingRuleIds.has(r.id) && <AlertTriangle size={13} className="text-amber-500" data-testid={`rule-overlap-flag-${r.id}`} title={t("mgmtPayroll.overlapTitle")} />}
                    </td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{r.applies_to_classification}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{r.store_location || t("mgmtPayroll.allStoresLabel")}{r.position ? ` · ${r.position}` : ""}</td>
                    <td className="px-4 py-2 text-gray-600">{r.rate_percent}%</td>
                    <td className="px-4 py-2 text-gray-500">v{r.version}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 space-x-1">
                      {r.status === "PENDING_APPROVAL" && canReviewPayroll && (
                        <>
                          <Button size="sm" data-testid={`rule-approve-btn-${r.id}`} onClick={() => ruleAction(r.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtPayroll.approve")}</Button>
                          <Button size="sm" variant="outline" data-testid={`rule-reject-btn-${r.id}`} onClick={() => openRejectRule(r)} className="text-red-600 border-red-200 hover:bg-red-50">{t("mgmtPayroll.reject")}</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="no-rules-row">{t("mgmtPayroll.noRules")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejectDialog && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setRejectDialog(null)} title={rejectDialog.title}
          contextItems={rejectDialog.contextItems} reasonLabel={t("mgmtPayroll.rejectionReason")} danger
          confirmLabel={rejectDialog.kind === "wage" ? t("mgmtPayroll.rejectWageTitle") : t("mgmtPayroll.rejectRuleTitle")}
          onConfirm={confirmReject} testId="payroll-reject-dialog"
        />
      )}
    </div>
  );
}

